import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useUIStore } from '../store/uiStore';

interface DropdownItem {
  name: string;
  description?: string;
  href: string;
  icon?: string;
}

interface MenuSection {
  title?: string;
  items: DropdownItem[];
}

interface MegaMenuContent {
  sections: MenuSection[];
  featured?: {
    title: string;
    description: string;
    href: string;
    cta: string;
  };
}

interface NavbarProps {
  sidebar?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ sidebar = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuthStore();
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const megaMenuRef = useRef<HTMLDivElement>(null);

  const productMenu: MegaMenuContent = {
    sections: [
      {
        title: 'Features',
        items: [
          { name: 'Real-time Collaboration', description: 'Work together instantly', href: '/features#realtime', icon: '⚡' },
          { name: 'Board Templates', description: '12+ ready-to-use formats', href: '/templates', icon: '📋' },
          { name: 'Facilitator Controls', description: 'Run better sessions', href: '/features#facilitator', icon: '🎛️' }
        ],
      },
      {
        title: 'Capabilities',
        items: [
          { name: 'Private Writing', description: 'Prevent groupthink', href: '/features#private', icon: '🔒' },
          { name: 'Anonymous Mode', description: 'Honest feedback', href: '/features#anonymous', icon: '🎭' },
          { name: 'Voting System', description: 'Prioritize ideas', href: '/features#voting', icon: '👍' },
          { name: 'AI Reports', description: 'Auto-generate summaries', href: '/ai-retrospective-summary', icon: '✨' },
        ],
      },
    ],
    featured: {
      title: 'New: AI Report Generator',
      description: 'Turn your sprint retro into an executive-ready report in seconds — themes, risks, and action items included.',
      href: '/ai-retrospective-summary',
      cta: 'Try AI Reports →',
    },
  };

  const solutionsMenu: MegaMenuContent = {
    sections: [
      {
        title: 'By Role',
        items: [
          { name: 'Scrum Masters', description: 'Facilitate better retros', href: '/features#scrum', icon: '🎯' },
          { name: 'Developers', description: 'Share feedback easily', href: '/features#dev', icon: '💻' },
          { name: 'Product Owners', description: 'Gather team insights', href: '/features#po', icon: '📊' },
          { name: 'Agile Coaches', description: 'Guide multiple teams', href: '/features#coach', icon: '🏆' },
        ],
      },
      {
        title: 'By Use Case',
        items: [
          { name: 'Sprint Retrospectives', description: 'Classic agile retros', href: '/templates', icon: '🔄' },
          { name: 'Team Health Checks', description: 'Monitor team wellbeing', href: '/templates', icon: '❤️' },
          { name: 'Project Reviews', description: 'Capture lessons learned', href: '/templates', icon: '📋' },
          { name: 'Brainstorming', description: 'Generate ideas together', href: '/templates', icon: '💡' },
        ],
      },
    ],
  };

  const resourcesMenu: MegaMenuContent = {
    sections: [
      {
        items: [
          { name: 'Blog', description: 'Tips and best practices', href: '/blog', icon: '✍️' },
          { name: 'Alternatives', description: 'Compare Retrosive to other tools', href: '/alternatives', icon: '⚖️' },
        ],
      },
    ],
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
      if (megaMenuRef.current && !megaMenuRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close menus on navigation
  useEffect(() => {
    setActiveMenu(null);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getUserInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user?.email) {
      return user.email.slice(0, 2).toUpperCase();
    }
    return user?.initials || 'U';
  };

  const getUserDisplayName = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user?.first_name) {
      return user.first_name;
    }
    if (user?.email) {
      return user.email;
    }
    return user?.display_name || 'User';
  };

  const renderMegaMenu = (menu: MegaMenuContent) => (
    <div className="absolute left-0 mt-2 w-screen max-w-4xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-50">
      <div className="grid grid-cols-12 gap-0">
        {/* Main sections */}
        <div className={`${menu.featured ? 'col-span-9' : 'col-span-12'} p-6`}>
          <div className="grid grid-cols-2 gap-8">
            {menu.sections.map((section, sectionIndex) => (
              <div key={sectionIndex}>
                {section.title && (
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                    {section.title}
                  </h3>
                )}
                <div className="space-y-1">
                  {section.items.map((item, itemIndex) => (
                    <Link
                      key={itemIndex}
                      to={item.href}
                      className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group"
                      onClick={() => setActiveMenu(null)}
                    >
                      <span className="text-2xl">{item.icon}</span>
                      <div>
                        <div className="font-medium text-slate-900 group-hover:text-orange-500 transition-colors">
                          {item.name}
                        </div>
                        {item.description && (
                          <div className="text-sm text-gray-500">{item.description}</div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Featured section */}
        {menu.featured && (
          <div className="col-span-3 bg-gradient-to-br from-orange-50 to-amber-50 p-6">
            <div className="h-full flex flex-col justify-between">
              <div>
                <span className="inline-block px-2 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full mb-3">
                  New
                </span>
                <h4 className="font-bold text-slate-900 mb-2">{menu.featured.title}</h4>
                <p className="text-sm text-slate-600">{menu.featured.description}</p>
              </div>
              <Link
                to={menu.featured.href}
                className="text-orange-500 font-semibold text-sm hover:text-orange-600 transition-colors"
                onClick={() => setActiveMenu(null)}
              >
                {menu.featured.cta}
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Sidebar mode for authenticated users (collapsible)
  if (sidebar && isAuthenticated) {
    const navItems = [
      { path: '/app/dashboard', label: 'Dashboard', icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      )},
      { path: '/app/teams', label: 'Teams', icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )},
      { path: '/app/actions', label: 'Actions', icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      )},
      { path: '/app/insights', label: 'AI Insights', icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      )},
      { path: '/app/organization', label: 'Organization', icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )},
      { path: '/app/profile', label: 'Profile', icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )},
    ];

    return (
      <>
      {/* Mobile top navbar for authenticated users (hidden on lg+) */}
      <nav className="lg:hidden sticky top-0 z-50 bg-white shadow-sm border-b border-gray-200">
        <div className="px-4">
          <div className="flex justify-between items-center h-16">
            <Link to="/app/dashboard" className="flex items-center gap-2">
              <img src="/logo.jpeg" alt="Retrosive" className="w-8 h-8 rounded-xl object-contain" />
              <span className="text-lg font-bold text-slate-900">Retrosive</span>
            </Link>
            <button
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        {isMobileMenuOpen && (
          <div className="border-t border-gray-200 px-4 py-4 space-y-1 bg-white shadow-xl">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                  location.pathname === item.path
                    ? 'bg-orange-50 text-orange-600'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
            ))}
            <div className="pt-3 border-t border-gray-200">
              <button
                onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign out
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Desktop sidebar (hidden on mobile) */}
      <nav
        className={`hidden lg:flex fixed left-0 top-0 h-screen bg-white border-r border-slate-200 z-40 flex-col transition-all duration-300 ease-in-out ${
          sidebarCollapsed ? 'w-[72px]' : 'w-64'
        }`}
      >
        {/* Logo */}
        <div className={`p-4 border-b border-slate-200 flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-2'}`}>
          <Link to="/app/dashboard" className="flex items-center overflow-hidden min-w-0">
            <img src="/logo.jpeg" alt="Retrosive" className="w-10 h-10 flex-shrink-0 rounded-xl object-contain" />
            {!sidebarCollapsed && (
              <span className="ml-2 text-xl font-bold text-slate-900 truncate">Retrosive</span>
            )}
          </Link>
        </div>

        {/* Navigation Links */}
        <div className="flex-1 py-4 overflow-y-auto">
          <div className={`space-y-1 ${sidebarCollapsed ? 'px-3' : 'px-3'}`}>
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const linkContent = (
                <Link
                  to={item.path}
                  className={`flex items-center rounded-lg text-sm font-medium transition-all ${
                    sidebarCollapsed ? 'justify-center px-3 py-2.5' : 'gap-3 px-4 py-2.5'
                  } ${
                    isActive ? 'bg-orange-50 text-orange-600' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {item.icon}
                  {!sidebarCollapsed && <span>{item.label}</span>}
                </Link>
              );
              return sidebarCollapsed ? (
                <div key={item.path} className="relative group">
                  {linkContent}
                  {/* Tooltip when collapsed */}
                  <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50 whitespace-nowrap shadow-xl">
                    {item.label}
                    <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-900" />
                  </div>
                </div>
              ) : (
                <div key={item.path}>{linkContent}</div>
              );
            })}
          </div>
        </div>

        {/* Toggle + User Menu */}
        <div className="border-t border-slate-200">
          {/* Collapse/Expand Toggle */}
          <div className={`flex ${sidebarCollapsed ? 'justify-center py-2' : 'px-3 pt-3'}`}>
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <svg
                className={`w-5 h-5 transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          </div>
          <div className={`${sidebarCollapsed ? 'p-3 pt-0' : 'p-4 pt-0'}`}>
          <div className="relative" ref={userDropdownRef}>
            <button
              onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
              className={`w-full flex items-center rounded-lg hover:bg-slate-50 transition-colors ${
                sidebarCollapsed ? 'justify-center px-0 py-2' : 'gap-3 px-3 py-2'
              }`}
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-medium shadow-md flex-shrink-0"
                style={{ backgroundColor: user?.avatar_color || '#6B7280' }}
              >
                {getUserInitials()}
              </div>
              {!sidebarCollapsed && (
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{getUserDisplayName()}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
              )}
              {!sidebarCollapsed && (
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </button>

            {isUserDropdownOpen && (
              <div className={`absolute bottom-full rounded-xl shadow-xl py-2 z-50 bg-white border border-slate-200 ${
                sidebarCollapsed ? 'left-full ml-2 min-w-[200px]' : 'left-0 mb-2 w-full'
              }`}>
                <div className="px-4 py-3 border-b border-slate-100">
                  <p className="text-sm font-medium text-gray-900">{getUserDisplayName()}</p>
                  <p className="text-sm truncate text-gray-500">{user?.email}</p>
                </div>
                <div className="py-2">
                  <Link
                    to="/app/profile"
                    className="flex items-center gap-3 px-4 py-2 text-sm transition-colors text-gray-700 hover:bg-slate-50"
                    onClick={() => setIsUserDropdownOpen(false)}
                  >
                    <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Profile Settings
                  </Link>
                </div>
                <div className="border-t pt-2 border-slate-100">
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm transition-colors text-red-600 hover:bg-red-50"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      </nav>
      </>
    );
  }

  // Top navbar mode (default for landing pages and unauthenticated)
  return (
    <nav className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to={isAuthenticated ? "/app/dashboard" : "/"} className="flex items-center gap-2">
              <img src="/logo.jpeg" alt="Retrosive" className="w-10 h-10 rounded-xl object-contain" />
              <span className="text-xl font-bold text-slate-900">
                Retrosive
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1" ref={megaMenuRef}>
            {!isAuthenticated && (
              <>
                {/* Product dropdown */}
                <div className="relative">
                  <button
                    onMouseEnter={() => setActiveMenu('product')}
                    onClick={() => setActiveMenu(activeMenu === 'product' ? null : 'product')}
                    className="flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-colors text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                  >
                    Product
                    <svg className={`w-4 h-4 transition-transform ${activeMenu === 'product' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {activeMenu === 'product' && renderMegaMenu(productMenu)}
                </div>

                {/* Solutions dropdown */}
                <div className="relative">
                  <button
                    onMouseEnter={() => setActiveMenu('solutions')}
                    onClick={() => setActiveMenu(activeMenu === 'solutions' ? null : 'solutions')}
                    className="flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-colors text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                  >
                    Solutions
                    <svg className={`w-4 h-4 transition-transform ${activeMenu === 'solutions' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {activeMenu === 'solutions' && renderMegaMenu(solutionsMenu)}
                </div>

                {/* Pricing link */}
                <Link
                  to="/pricing"
                  className="px-4 py-2 rounded-lg font-medium transition-colors text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                >
                  Pricing
                </Link>

                {/* Resources dropdown */}
                <div className="relative">
                  <button
                    onMouseEnter={() => setActiveMenu('resources')}
                    onClick={() => setActiveMenu(activeMenu === 'resources' ? null : 'resources')}
                    className="flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-colors text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                  >
                    Resources
                    <svg className={`w-4 h-4 transition-transform ${activeMenu === 'resources' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {activeMenu === 'resources' && (
                    <div className="absolute left-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 p-4 z-50">
                      <div className="space-y-1">
                        {resourcesMenu.sections[0].items.map((item, index) => (
                          <Link
                            key={index}
                            to={item.href}
                            className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                            onClick={() => setActiveMenu(null)}
                          >
                            <span className="text-xl">{item.icon}</span>
                            <div>
                              <div className="font-medium text-gray-900">{item.name}</div>
                              <div className="text-sm text-gray-500">{item.description}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {isAuthenticated && (
              <>
                <Link
                  to="/app/dashboard"
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                >
                  Dashboard
                </Link>
                <Link
                  to="/app/actions"
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                >
                  Actions
                </Link>
                <Link
                  to="/app/teams"
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                >
                  Teams
                </Link>
              </>
            )}
          </div>

          {/* Right side - Auth buttons / User menu */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <div className="relative" ref={userDropdownRef}>
                <button
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  className="flex items-center gap-2 transition-colors text-gray-700 hover:text-gray-900"
                >
                  <div 
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-medium shadow-md"
                    style={{ backgroundColor: user?.avatar_color || '#6B7280' }}
                  >
                    {getUserInitials()}
                  </div>
                  <span className="hidden md:block text-sm font-medium">
                    {getUserDisplayName()}
                  </span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isUserDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-xl shadow-xl py-2 z-50 bg-white border border-gray-200">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{getUserDisplayName()}</p>
                      <p className="text-sm truncate text-gray-500">{user?.email}</p>
                    </div>
                    
                    <div className="py-2">
                      <Link
                        to="/app/profile"
                        className="flex items-center gap-3 px-4 py-2 text-sm transition-colors text-gray-700 hover:bg-gray-50"
                        onClick={() => setIsUserDropdownOpen(false)}
                      >
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Profile Settings
                      </Link>
                    </div>
                    
                    <div className="border-t pt-2 border-gray-100">
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 w-full px-4 py-2 text-sm transition-colors text-red-600 hover:bg-red-50"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden lg:flex items-center gap-3">
                <Link
                  to="/login"
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold hover:shadow-lg transition-all"
                >
                  Sign Up
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-xl">
          <div className="px-4 py-6 space-y-4">
            {!isAuthenticated && (
              <>
                <Link
                  to="/features"
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Features
                </Link>
                <Link
                  to="/templates"
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Templates
                </Link>
                <Link
                  to="/pricing"
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Pricing
                </Link>
                <Link
                  to="/about"
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  About
                </Link>
                <Link
                  to="/blog"
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Blog
                </Link>
                <Link
                  to="/ai-retrospective-summary"
                  className="flex items-center gap-2 px-4 py-2 text-orange-600 hover:bg-orange-50 rounded-lg font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span>✨</span> AI Reports
                </Link>
                <div className="pt-4 border-t border-gray-200 space-y-3">
                  <Link
                    to="/login"
                    className="block w-full text-center px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/register"
                    className="block w-full text-center px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </div>
              </>
            )}
            {isAuthenticated && (
              <>
                <Link
                  to="/app/dashboard"
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <Link
                  to="/app/actions"
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Actions
                </Link>
                <Link
                  to="/app/teams"
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Teams
                </Link>
                <Link
                  to="/app/profile"
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Profile
                </Link>
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg font-medium"
                  >
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
